import { EnvelopeIcon } from '@heroicons/react/16/solid';
import { useMemo } from 'react';

import { format } from 'date-fns';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import {
  FileAttachment,
  LinkAttachment,
  UserAvatar
} from 'src/shared/components';
import { CustomFile, CustomLink, Reply } from 'src/shared/types/update';

import ImageGallery from 'src/shared/components/ImageGallery';
import { Typography } from 'src/shared/components/ui';

import findUserInOrganization from '../../utils/findUserInOrganization';
import isFileImage from '../../utils/isFileImage';
import MarkdownContent from './markdown-content/MarkdownContent';

interface UpdateReplyContentProps {
  reply: Reply;
  isLast: boolean;
}
const UpdateReplyContent: React.FC<UpdateReplyContentProps> = ({ reply }) => {
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const replyUser = reply.user_id
    ? findUserInOrganization(selectedOrganization, reply.user_id)
    : null;

  const imageUrls = useMemo(() => {
    return reply.files.filter(isFileImage);
  }, [reply.files]);

  const files = useMemo(() => {
    return reply.files.filter((file) => !isFileImage(file));
  }, [reply.files]);

  const renderFiles = () => {
    return files.map((file: CustomFile) => (
      <FileAttachment key={file.id} fileName={file.name} fileUrl={file.url} />
    ));
  };

  const renderLinks = () => {
    return reply.links.map((link: CustomLink, index) => (
      <LinkAttachment key={index} url={link.url} caption={link.caption} />
    ));
  };

  const withAttachments = useMemo(() => {
    return reply.files.length > 0 || reply.links.length > 0;
  }, [reply.files, reply.links]);

  const replyAvatar = useMemo(() => {
    if (replyUser) {
      return replyUser.avatar_url ?? undefined;
    }

    if (reply.is_staff) {
      return '/images/defaultCavela.svg';
    }

    return undefined;
  }, [replyUser, reply.is_staff]);

  return (
    <div className="relative flex gap-2 items-start mt-2 py-2">
      {/* {isLast && (
        <div className="w-[2.3px] rounded-full top-0 bottom-0 left-[-39px] right-0 absolute z-10 bg-white" />
      )} */}

      <div
        style={{ rotate: '180deg' }}
        className="w-[40px] h-[40px] top-[-8px] left-[-38px] absolute z-10"
      >
        <svg width="40" height="40" viewBox="0 0 40 40">
          <path
            d="M0,0 A40,40 0 0,1 40,40"
            stroke="#dee2e6"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="border-3 border-white rounded-full">
        <UserAvatar
          image={replyAvatar}
          name={replyUser?.username || 'Cavela'}
          disableAnimation
          withTooltip
        />
      </div>
      <div className="mt-2 flex flex-col w-full min-w-0">
        <div className="flex-1 flex gap-[2px] min-w-0">
          <div className="flex-1 flex-col flex gap-1 w-full min-w-0">
            <MarkdownContent markdownString={reply.reply} />
            {withAttachments && (
              <div className="flex gap-2 mt-1 items-center flex-wrap">
                {imageUrls.length > 0 && <ImageGallery imageUrls={imageUrls} />}
                {files.length > 0 && renderFiles()}
                {reply.links.length > 0 && renderLinks()}
              </div>
            )}

            {reply.created_at && (
              <div className="flex items-center gap-1 mt-1">
                {reply.reply_source?.sendgrid_id && (
                  <EnvelopeIcon
                    className="h-4 w-4 text-secondary-text"
                    title="Received via email"
                  />
                )}
                <Typography variant="body-2" className="text-secondary-text">
                  {format(reply.created_at, 'MMM dd, yyyy')}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateReplyContent;
